import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CourseHero from './CourseHero';
import CourseSyllabus from './CourseSyllabus';
import CourseFederationBadge from './CourseFederationBadge';
import CoursePricing from './CoursePricing';
import CourseCTA from './CourseCTA';
import CourseTutorPanel from './CourseTutorPanel';
import RedeemCodePanel from './RedeemCodePanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tutorOpen, setTutorOpen] = useState(false);
  const [tutorMessages, setTutorMessages] = useState([]);
  const [tutorInput, setTutorInput] = useState('');
  const [tutorLoading, setTutorLoading] = useState(false);
  const [tutorSessionId] = useState(() => `tutor_${courseId}_${Date.now()}`);

  const fetchCourseDetails = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/courses/${courseId}`);
      if (response.ok) setCourse(await response.json());
    } catch (e) {
      /* swallow */
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchCourseDetails();
  }, [fetchCourseDetails]);

  const openTutor = () => {
    setTutorOpen(true);
    if (tutorMessages.length === 0 && course) {
      setTutorMessages([
        {
          id: 1,
          type: 'tutor',
          text: `Hello! I'm your AI teaching assistant for "${course.title}". I know this course inside and out. Ask me anything about the topics, modules, or concepts — I'm here to help you learn!`,
          timestamp: new Date(),
        },
      ]);
    }
  };

  const askTutor = async () => {
    if (!tutorInput.trim() || tutorLoading) return;

    const userQuestion = tutorInput;
    setTutorMessages((prev) => [
      ...prev,
      { id: prev.length + 1, type: 'user', text: userQuestion, timestamp: new Date() },
    ]);
    setTutorInput('');
    setTutorLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/courses/tutor/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course_id: courseId,
          question: userQuestion,
          session_id: tutorSessionId,
        }),
      });
      if (!response.ok) throw new Error('Tutor request failed');
      const data = await response.json();
      setTutorMessages((prev) => [
        ...prev,
        { id: prev.length + 1, type: 'tutor', text: data.response, timestamp: new Date() },
      ]);
    } catch (e) {
      setTutorMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          type: 'tutor',
          text: "I apologize, I'm having trouble connecting right now. Please try again in a moment.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setTutorLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">Course Not Found</h1>
          <button
            onClick={() => navigate('/')}
            data-testid="course-not-found-back"
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg"
          >
            Back to Catalog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <CourseHero course={course} onBack={() => navigate('/')} onOpenTutor={openTutor} />
      <CourseSyllabus modules={course.modules} />
      <CourseFederationBadge course={course} />
      <CoursePricing priceUsd={course.price} />
      <RedeemCodePanel courseId={course.course_id || courseId} />
      <CourseCTA course={course} onBrowseMore={() => navigate('/')} />
      <CourseTutorPanel
        open={tutorOpen}
        courseTitle={course.title}
        messages={tutorMessages}
        input={tutorInput}
        loading={tutorLoading}
        onInputChange={setTutorInput}
        onSend={askTutor}
        onClose={() => setTutorOpen(false)}
      />
    </div>
  );
}
